import { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import Skeleton from "react-loading-skeleton";
import { showToast } from "../../utils/helpers"; // Updated import
import { TextInput } from "../FormInputs/TextInput";
import { EditUserProfileStyled } from "./styled";
import {
	changePasswordActions,
	update_user,
} from "../../actions/AccountManagementActions";
import { OnSubmitValidationError } from "../../utils/OnSubmitValidationError";
import { TabHeader } from "../TabHeader";
import { Select } from "../FormInputs/Select";
import { SocialInvitation } from "../SocialInvitation/SocialInvitation";
import { getPasswordStrength } from "../../utils/helpers";
import { PasswordValidator } from "../FormInputs/PasswordValidator";
import { FlashError } from "../FormInputs/FlashError";
import { Button } from "components/Button";
import FormHeader from "components/FormHeading/FormHeader";
import { Icon } from "assets/Icon";
import { Spinner } from "@chakra-ui/react";
import { USER_ROLE, USER_ROLES_FOR_SELECTION } from "newui/features/Auth/constants/UserRoleConstants";
import { useCurrentUser } from "hooks/auth";
import { getUserRoleByValue } from "newui/features/Auth/utils";
import PasswordEntry from "newui/features/Auth/components/PasswordEntry";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { PASSWORD_RULES } from "newui/features/Auth/components/PasswordEntry/PasswordEntry";
import { MfaSettingsSection } from "newui/features/Settings/components/Security/MfaSettingsSection";
import { useHasTwoFactorAuth } from "hooks/featureFlags";

export const EditUserProfileComponent = ({
	viewOnly,
	changePasswordActions,
	...props
}) => {
	const [isChangingPassword, setIsChangingPassword] = useState(false);
	const [passwordRules] = useState({ letter: false });
	const [passwordRulesConfirmation] = useState({ letter: false });
	const { currentUser } = useCurrentUser();
	const hasTwoFactorAuth = useHasTwoFactorAuth();
	const userRole = currentUser.company_admin ? USER_ROLE.ADMINISTRATOR : USER_ROLE.USER;

	const initialValues = {
		id: currentUser?.id,
		first_name: currentUser?.first_name || "",
		last_name: currentUser?.last_name || "",
		phone: currentUser?.phone || "",
		email: currentUser?.email || "",
		role: userRole.name || "",
	};

	const validationSchema = Yup.object({
		first_name: Yup.string().required("This is a required field"),
		last_name: Yup.string().required("This is a required field"),
		email: Yup.string().required("This is a required field"),
		phone: Yup.string()
			.required("This is a required field")
			.matches(
				/^(?=.*)((?:\+?61) ?(?:\((?=.*\)))?([2-47-8])\)?|(?:\((?=.*\)))?([0-1][2-47-8])\)?) ?-?(?=.*)((\d{1} ?-?\d{3}$)|(00 ?-?\d{4} ?-?\d{4}$)|( ?-?\d{4} ?-?\d{4}$)|(\d{2} ?-?\d{3} ?-?\d{3}$))/,
				"Invalid Phone No."
			),
		role: Yup.string().required("This is a required field"),
	});

	const handleChangePasswordSubmit = async (values, helpers) => {
		try {
			const passwordChanged = await changePasswordActions(
				values,
				currentUser.id
			);
			if (passwordChanged) {
				setIsChangingPassword(false);
				helpers.resetForm();
			}
		} catch (e) {
			// errors handled
			return;
		}
	};

	return currentUser !== undefined ? (
		<EditUserProfileStyled>
			<Formik
				enableReinitialize
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={async (values, { setErrors }) => {
					try {
						await props.update_user(values, true);
						showToast('Updated user Profile', 'Success', true);
					} catch (e) {
						const error = await e.response.json();
						showToast(error.message, "Error");
						error.errors !== undefined && setErrors(error.errors);
					}
				}}
			>
				{(props) => {
					const { isSubmitting, values, status, setStatus, isValid } = props;
					const onSubmitValidationError = (e) => {
						setStatus("Please review errors in form");
					};
					const submitHandler = async () => {
						if (!isSubmitting) {
							await props.submitForm();
						}
					};
					return (
						<>
							<TabHeader heading="My Profile">
								<Button
									data-testid="save-details-button"
									style={{ height: "48px" }}
									onClick={submitHandler}
									disabled={!isValid || isSubmitting}
								>
									{isSubmitting ? (
										<div className="flex items-center gap-2">
											<Spinner /> Saving
										</div>
									) : (
										"Save Details"
									)}
								</Button>
							</TabHeader>

							<Form>
								<div className="row">
									<div style={{ marginLeft: "-46px", marginRight: "-46px" }}>
										<div className="grid grid-cols-12 gap-6 gap-y-0">
											<FormHeader
												className="col-span-12 mb-6"
												header="Your Details"
												subHeader="Update your personal details here."
											/>
											<TextInput
												label="First Name"
												placeholder="Enter your first name"
												required={!viewOnly}
												name="first_name"
												className="col-span-6"
												disabled={viewOnly}
												data-testid="first-name-input"
											/>
											<TextInput
												label="Last Name"
												placeholder="Enter your last name"
												required={!viewOnly}
												name="last_name"
												className="col-span-6"
												disabled={viewOnly}
												data-testid="last-name-input"
											/>
											<TextInput
												label="Phone Number"
												placeholder="Enter your phone number"
												required={!viewOnly}
												name="phone"
												className="col-span-6"
												disabled={viewOnly}
												data-testid="phone-number-input"
											/>
											<TextInput
												label="Email Address"
												placeholder="Enter your email address"
												required={!viewOnly}
												name="email"
												className="col-span-6"
												disabled
											/>
											<Select
												required="true"
												options={USER_ROLES_FOR_SELECTION}
												name="role"
												label={"User Role"}
												className={"col-span-6"}
												value={getUserRoleByValue(initialValues.role)}
												disabled
											/>
											<div className="col-span-6">
												{status && (
													<FlashError
														heading={"Required Field"}
														text={
															"There are incomplete required fields. Please complete them."
														}
														margin="20px 0px 0px 0px"
													/>
												)}
											</div>
											{!viewOnly && (
												<>
													<div className="social-invitations">
														<SocialInvitation />
													</div>
												</>
											)}
										</div>
									</div>
								</div>
								<OnSubmitValidationError callback={onSubmitValidationError} />
							</Form>
						</>
					);
				}}
			</Formik>
			<div className="flex flex-row gap-20 mt-8 w-full">
				<div className="mb-14">
					<Formik
						enableReinitialize
						initialValues={{
							password: "",
							confirmationPassword: "",
							current_password: "",
						}}
						onSubmit={handleChangePasswordSubmit}
					>
						{({ setFieldValue, isValid, resetForm, isSubmitting }) => {
							const handleCancel = () => {
								resetForm();
								setIsChangingPassword(false);
							};

							return (
								<Form style={{ padding: 0 }}>
									<FormHeader
										className="mb-4"
										header="Change Password"
										subHeader="This your personal password linked to your Procuracon account."
									/>
									{!isChangingPassword ? (
										<Button
											btntype="base"
											type="button"
											className="items-center gap-2"
											data-testid="open-password-modal-button"
											onClick={() => setIsChangingPassword(true)}
											style={{ height: "48px" }}
										>
											<Icon icon="lock" />
											Change password
										</Button>
									) : (
										<div className="w-full">
											<div className="mb-4"><InstructionsInfoBox title="Password Rules..." instructions={PASSWORD_RULES} /></div>
											<PasswordEntry passwordOverride={{ id: "current_password", name: "current_password", label: "Current Password" }} />
											<PasswordEntry showConfirmPassword />
											<PasswordValidator
												dataId={"pass_validation"}
												passwordRules={passwordRules}
											/>
											<PasswordValidator
												dataId={"pass_validation_confirmation"}
												passwordRules={passwordRulesConfirmation}
											/>

											<div className="mt-8 flex items-center gap-4">
												<Button
													btntype="base"
													type="button"
													onClick={handleCancel}
													className="max-h-12"
												>
													Cancel
												</Button>
												<Button
													disabled={!isValid || isSubmitting}
													className="max-h-12"
													data-testid="submit-password-button"
												>
													{" "}
													Change Password
												</Button>
											</div>
										</div>
									)}
								</Form>
							);
						}}
					</Formik>
				</div>
				{!viewOnly && hasTwoFactorAuth && (
					<div className="flex-1" data-testid="profile-mfa-section">
						<FormHeader
							className="mb-4"
							header="Two-Factor Authentication"
							subHeader="Add a second step at login using an authenticator app."
						/>
						{/* Rendered OUTSIDE the profile <Form>: the enrolment wizard has its own
					    nested <form>, and nesting forms triggers a native submit/page reload. */}
						<MfaSettingsSection />
					</div>
				)}
			</div>
		</EditUserProfileStyled>
	) : (
		<Skeleton
			count={5}
			duration={0.5}
		/>
	);
};

export const EditUserProfile = connect(null, {
	update_user,
	changePasswordActions,
})(EditUserProfileComponent);
